import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Alert, SelectChangeEvent, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { ImportExport, RestartAlt } from "@mui/icons-material";


import { MindsetListSkeleton } from "./MindsetListSkeleton";
import {
  ButtonIcon,
  FilterBox,
  MindsetCard,
  SearchBox,
} from "@/components/atoms";
import { PageContainer } from "@/components/containers";
import { useAudienceDispatch } from "@/context";
import { useAudiences } from "@/hooks";
import { MindsetAudience } from "@/types";
import { audienceSortOptions, filterAudiences } from "@/utils";


const MindsetListPage: React.FC = () => {
  const dispatch = useAudienceDispatch();
  const navigate = useNavigate();

  const { audiences, loading, error } = useAudiences("mindset");

  const [rows, setRows] = useState<MindsetAudience[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [sortBy, setSortBy] = useState<string>("");

  useEffect(() => {
    const filteredAudiences = filterAudiences(
      audiences,
      searchText,
      undefined, // No filter
      sortBy
    );

    if (filteredAudiences) setRows(filteredAudiences as MindsetAudience[]);
  }, [audiences, searchText, sortBy]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) =>
    setSearchText(event.target.value || "");

  const handleSortSelect = (event: SelectChangeEvent<string | string[]>) =>
    setSortBy(event.target.value as string);

  const handleReset = () => {
    setSearchText("");
    setSortBy("");
  };

  const handleAudienceSelect = (audience: MindsetAudience) => {
    dispatch({ type: "GET_AUDIENCE", payload: { audience } });
    navigate(`${audience.audience_id}`);
  };

  return (
    <PageContainer>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Typography>Mindsets</Typography>
        </Grid>
        <Grid container size={12} alignItems="center">
          <Grid>
            <SearchBox value={searchText} onSearch={handleSearch} />
          </Grid>
          <Grid>
            <FilterBox
              label="Sort"
              value={sortBy}
              options={audienceSortOptions}
              onChange={handleSortSelect}
              startIcon={<ImportExport fontSize="small" />}
              showSelected
            />
          </Grid>
          <Grid>
            <ButtonIcon
              onClick={handleReset}
              color="info"
              size={38}
              Icon={RestartAlt}
              tooltip="Reset Filters"
            />
          </Grid>
        </Grid>

        <Grid container size={12} mb={4}>
          {loading ? (
            <MindsetListSkeleton />
          ) : error ? (
            <Grid>
              <Alert variant="filled" severity="error">
                It was not possible to load your audiences at this time, please
                try again.
              </Alert>
            </Grid>
          ) : (
            rows.map((audience, index) => (
              <Grid key={index}>
                <MindsetCard
                  mindset={audience}
                  onSelect={handleAudienceSelect}
                />
              </Grid>
            ))
          )}
        </Grid>
      </Grid>
    </PageContainer>
  );
};

export default MindsetListPage;
